import type { EventDefinition } from "./catalog";

/**
 * Exemplos de conteúdo, um por evento.
 *
 * Quem cadastra um destino precisa saber o que vai receber ANTES de escrever
 * código do outro lado. Sem isso, a única forma de descobrir o formato é
 * cadastrar, esperar acontecer, e ler o que chegou — e enquanto isso o
 * integrador está adivinhando.
 *
 * Os payloads são interfaces TypeScript: existem em tempo de compilação e
 * somem no runtime, então não há como derivar exemplo delas. A saída é
 * declarar — mas declarando as ENTIDADES uma vez e compondo os eventos a
 * partir delas, senão viram 45 blocos de JSON que envelhecem cada um por
 * conta própria.
 *
 * Os valores são fictícios de propósito (número 5511999999999, "Maria
 * Silva"): é exemplo de formato, não dado de ninguém.
 */

const CONTATO = {
  id: 42,
  name: "Maria Silva",
  number: "5511999999999",
  email: "maria@exemplo.com.br",
  isGroup: false,
  companyId: 1
};

const TICKET = {
  id: 1234,
  uuid: "a1b2c3d4-0000-0000-0000-000000000000",
  status: "open",
  isGroup: false,
  channel: "whatsapp",
  unreadMessages: 0,
  contactId: CONTATO.id,
  userId: 7,
  queueId: 3,
  whatsappId: 2,
  companyId: 1,
  isBot: false,
  contact: CONTATO,
  createdAt: "2026-08-04T14:29:55.000Z",
  updatedAt: "2026-08-04T14:30:00.000Z"
};

const MENSAGEM = {
  id: 98765,
  wid: "3EB0C767D26B8A3F1B2C",
  body: "Boa tarde, preciso de ajuda com meu pedido",
  fromMe: false,
  source: "contact",
  ticketId: TICKET.id,
  contactId: CONTATO.id,
  companyId: 1,
  mediaType: "chat",
  mediaUrl: null,
  createdAt: "2026-08-04T14:30:00.000Z"
};

const CONEXAO = {
  id: 2,
  name: "Comercial",
  channel: "whatsapp",
  status: "CONNECTED",
  number: "5511988887777",
  isDefault: true,
  companyId: 1
};

const USUARIO = {
  id: 7,
  name: "João Atendente",
  email: "joao@empresa.com.br",
  profile: "user",
  online: true,
  companyId: 1
};

const TAG = { id: 5, name: "Urgente", color: "#e74c3c", companyId: 1 };

const EMPRESA = {
  id: 88,
  name: "Empresa Nova Ltda",
  email: "contato@empresanova.com.br",
  status: true,
  createdAt: "2026-08-04T14:30:00.000Z"
};

/** Exemplo por evento. O que não estiver aqui cai no genérico da tela. */
export const EVENT_SAMPLES: Record<string, Record<string, any>> = {
  // ─── Atendimento ───
  "ticket:created": { ticket: TICKET, companyId: 1 },
  "ticket:updated": {
    ticket: TICKET,
    companyId: 1,
    changes: { status: { from: "pending", to: "open" } }
  },
  "ticket:assigned": {
    ticket: TICKET,
    companyId: 1,
    userId: USUARIO.id,
    oldUserId: null
  },
  "ticket:transferred": {
    ticket: TICKET,
    companyId: 1,
    oldQueueId: 1,
    newQueueId: 3,
    oldUserId: 4,
    newUserId: 7
  },
  "ticket:closed": {
    ticket: { ...TICKET, status: "closed" },
    companyId: 1,
    oldStatus: "open"
  },
  "ticket:reopened": { ticket: TICKET, companyId: 1, oldStatus: "closed" },
  "ticket:deleted": { ticket: TICKET, companyId: 1 },
  "ticket:tagged": { ticket: TICKET, tag: TAG, companyId: 1 },
  "ticket:untagged": { ticket: TICKET, tag: TAG, companyId: 1 },

  // ─── Mensagem ───
  "ticket:messageReceived": {
    message: MENSAGEM,
    ticket: TICKET,
    contact: CONTATO,
    companyId: 1
  },
  "ticket:messageSent": {
    message: {
      ...MENSAGEM,
      id: 98766,
      body: "Claro! Já verifico para você.",
      fromMe: true,
      source: "agent",
      sourceId: USUARIO.id
    },
    ticket: TICKET,
    companyId: 1
  },
  "ticket:messageEdited": {
    message: { ...MENSAGEM, body: "Boa tarde, preciso de ajuda (corrigido)" },
    ticket: TICKET,
    companyId: 1,
    oldBody: "Boa tarde, preciso de ajuda com meu pedido"
  },
  "ticket:messageDeleted": { message: MENSAGEM, ticket: TICKET, companyId: 1 },
  "ticket:messageReacted": {
    message: MENSAGEM,
    ticket: TICKET,
    companyId: 1,
    reaction: "👍"
  },
  "message:sent": { message: { ...MENSAGEM, fromMe: true }, companyId: 1 },
  "message:edited": { message: MENSAGEM, companyId: 1 },
  "message:deleted": { message: MENSAGEM, companyId: 1 },
  "message:reacted": { message: MENSAGEM, companyId: 1, reaction: "👍" },
  "message:ackChanged": {
    message: MENSAGEM,
    companyId: 1,
    ack: 3,
    previousAck: 2
  },
  "message:mediaRetried": {
    message: { ...MENSAGEM, mediaType: "image", mediaUrl: "/public/arquivo.jpg" },
    companyId: 1
  },
  "message:received": { message: MENSAGEM, companyId: 1 },

  // ─── Contato ───
  "contact:created": { contact: CONTATO, companyId: 1 },
  "contact:updated": {
    contact: CONTATO,
    companyId: 1,
    changes: { name: { from: "Maria", to: "Maria Silva" } }
  },
  "contact:deleted": { contact: CONTATO, companyId: 1 },
  "contact:tagged": { contact: CONTATO, tag: TAG, companyId: 1 },
  "contact:untagged": { contact: CONTATO, tag: TAG, companyId: 1 },

  // ─── Conexão ───
  "connection:created": { connection: CONEXAO, companyId: 1 },
  "connection:updated": { connection: CONEXAO, companyId: 1 },
  "connection:deleted": { connection: CONEXAO, companyId: 1 },
  "connection:statusChanged": {
    connection: CONEXAO,
    companyId: 1,
    status: "CONNECTED",
    previousStatus: "OPENING"
  },
  "connection:disconnected": {
    connection: { ...CONEXAO, status: "DISCONNECTED" },
    companyId: 1,
    reason: "logged_out"
  },
  "connection:qrcodeReceived": {
    connection: { ...CONEXAO, status: "qrcode" },
    companyId: 1,
    qrcode: "2@A1b2C3d4E5f6..."
  },
  "connection:rateLimited": { connection: CONEXAO, companyId: 1, retryAfter: 30 },

  // ─── Usuário ───
  "user:created": { user: USUARIO, companyId: 1 },
  "user:updated": {
    user: USUARIO,
    companyId: 1,
    changes: { profile: { from: "user", to: "admin" } }
  },
  "user:deleted": { user: USUARIO, companyId: 1 },

  // ─── Plataforma ───
  "company:created": { company: EMPRESA },
  "company:updated": {
    company: EMPRESA,
    changes: { name: { from: "Empresa Nova", to: "Empresa Nova Ltda" } }
  }
};

/**
 * O exemplo de um evento — do catálogo do core ou declarado pelo plugin.
 *
 * O do plugin vence: ele é quem sabe o que manda. Sem exemplo nenhum, devolve
 * `undefined` e a tela mostra o formato genérico, que ao menos ensina o
 * envelope.
 */
export function sampleOf(evento?: EventDefinition): Record<string, any> | undefined {
  if (!evento) return undefined;
  return evento.sample || EVENT_SAMPLES[evento.name];
}
